// ─── Service « Journal de bord » ──────────────────────────────────
// Notes datées partagées d'un calendrier — transmettre une information du
// quotidien (santé, école, incident…) entre les personnes du calendrier.
// Différent de la messagerie : les notes PERSISTENT et s'organisent
// chronologiquement au lieu de défiler dans un fil.
//
// Collection `journal/{id}` :
//   { calendrierId, date, texte, nature, valeurId, valeurLibre,
//     auteurUid, auteurNom, creeLe, modifieLe? }
//   nature      : 'note' | 'humeur' | 'incident' — désigne le VOCABULAIRE.
//   valeurId    : entrée choisie dans ce vocabulaire.
//   valeurLibre : texte saisi à la main, valable pour CETTE note seulement.
//   Les deux s'excluent — c'est ce qui garde « choisir » et « écrire »
//   distincts dans la DONNÉE, et pas seulement à l'écran.
// Règles : accesCalId (participant du calendrier OU membre de l'espace),
// comme les contacts et les lieux.
import { db } from '@mop/core';
import { collection, addDoc, deleteDoc, doc, updateDoc, onSnapshot, query, where } from 'firebase/firestore';
import { normaliserNote } from '../data/journal';

// La normalisation est PURE et vit dans data/journal.js (testable sans
// Firestore) ; on la réexporte pour que les écrans n'aient qu'une porte.
export { normaliserNote };

// ── Écoute ───────────────────────────────────────────────────────
// `onErreur` est OBLIGATOIREMENT remonté à l'appelant. Le service MoPCal
// répondait `callback([])` sur refus de lecture : l'écran affichait alors une
// liste vide SANS RIEN SIGNALER, et une note bien enregistrée passait pour
// inexistante (piège documenté dans COWORK.md). On ne l'avale pas.
export function ecouterNotes(calendrierId, callback, onErreur) {
  if (!calendrierId) { callback([]); return () => {}; }
  const q = query(collection(db, 'journal'), where('calendrierId', '==', calendrierId));
  return onSnapshot(
    q,
    (snap) => {
      const l = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      // Tri client : la plus récente d'abord, `creeLe` départage deux notes
      // du même jour. Pas d'orderBy → pas d'index composite à déployer.
      l.sort((a, b) => String(b.date || '').localeCompare(String(a.date || ''))
        || String(b.creeLe || '').localeCompare(String(a.creeLe || '')));
      callback(l);
    },
    (e) => { onErreur?.(e?.message || String(e)); },
  );
}

// ── Écriture ─────────────────────────────────────────────────────
export async function ajouterNote({ calendrierId, auteurUid, auteurNom, ...note }) {
  if (!calendrierId) throw new Error('Calendrier absent');
  const n = normaliserNote(note);
  if (!n.texte) throw new Error('Note vide');
  await addDoc(collection(db, 'journal'), {
    calendrierId,
    ...n,
    auteurUid: auteurUid || null,
    auteurNom: auteurNom || '',
    creeLe: new Date().toISOString(),
  });
}

// Édition d'une note existante. MoPCal exposait `modifierNote` mais ne
// l'appelait nulle part — l'édition n'a jamais été câblée. Ici elle l'est,
// depuis la fiche (principe app-wide : tap = fiche, édition depuis la fiche).
export async function modifierNote(id, note) {
  if (!id) throw new Error('Note inconnue');
  const n = normaliserNote(note);
  if (!n.texte) throw new Error('Note vide');
  await updateDoc(doc(db, 'journal', id), { ...n, modifieLe: new Date().toISOString() });
}

export async function supprimerNote(id) {
  if (!id) return;
  await deleteDoc(doc(db, 'journal', id));
}
